// Bank Account services mock (spec §5, §9). Cheques, re-KYC, GST, FATCA,
// favourites, IMPS/other disputes + fraud, lockers, nominee. Every money
// movement writes a statement row and a notification; re-KYC reminders and the
// debit restriction follow the bank clock.
import { loadDb, saveDb, type MockDb } from '../db';
import { mockNow } from '../clock';
import {
  addNotification,
  addStatementRow,
  assertDebitAllowed,
  makeReference,
  rekycRestrictsDebits,
  registerClockStep,
} from '../phase4';
import type {
  ChequeBook,
  ChequeLeaf,
  PositivePayEntry,
  RekycState,
  GstRegistration,
  FatcaRecord,
  Favourite,
  Dispute,
  Locker,
  LockerBranch,
  LockerSize,
  LockerWaitlist,
} from '@/types/phase4.types';

let seq = 0;
const nid = (p: string): string => `${p}_${Date.now().toString(36)}_${(seq++).toString(36)}`;
const DAY = 24 * 3_600_000;

// ---------- account facts ----------

export const accountFacts = (): { productType: string; pan: string } => {
  const db = loadDb();
  return { productType: db.productType, pan: db.loans.pan };
};

// ============================================================ CHEQUES (§5.1)

// Seed the delivered welcome-kit book on first read (spec §5.1).
const ensureWelcomeBook = (db: MockDb): void => {
  if (db.phase4.chequeBooks.length > 0) return;
  const leaves = db.productType === 'CURRENT' ? 50 : 25;
  const book: ChequeBook = {
    id: nid('cb'),
    leaves,
    from: 100001,
    to: 100001 + leaves - 1,
    stage: 'DELIVERED',
    reference: makeReference('CB'),
    requestedAt: mockNow(db),
    delivered: true,
    leafList: Array.from({ length: leaves }, (_, i) => ({ number: 100001 + i, status: 'UNUSED' as const })),
  };
  db.phase4.chequeBooks.push(book);
  saveDb(db);
};

export const chequeBooks = (): Promise<ChequeBook[]> => {
  const db = loadDb();
  ensureWelcomeBook(db);
  return Promise.resolve(db.phase4.chequeBooks);
};

export const chequeLeaves = (bookId: string): Promise<ChequeLeaf[]> => {
  const db = loadDb();
  ensureWelcomeBook(db);
  const book = db.phase4.chequeBooks.find((b) => b.id === bookId);
  return Promise.resolve(book?.leafList ?? []);
};

const inProgressBook = (db: MockDb): ChequeBook | undefined =>
  db.phase4.chequeBooks.find((b) => !b.delivered);

export const chequeBookQuote = (leaves: number): Promise<{ freeLeft: number; costRupees: number; gstRupees: number; blockedReason?: string }> => {
  const db = loadDb();
  ensureWelcomeBook(db);
  const now = mockNow(db);
  const inProgress = inProgressBook(db);
  let blockedReason: string | undefined;
  if (inProgress) blockedReason = 'A cheque book request is already in progress.';
  else if (db.limits.debitsFrozen) blockedReason = "Cheque books can't be issued while the account is frozen or dormant.";

  // 1 free book in the last 365 days (welcome book excluded, spec §5.1).
  const yearAgo = now - 365 * DAY;
  const paidBooks = db.phase4.chequeBooks.filter((b) => b.id !== db.phase4.chequeBooks[0]?.id && b.requestedAt >= yearAgo);
  const freeLeft = paidBooks.length === 0 ? 1 : 0;
  const costRupees = freeLeft > 0 ? 0 : 3 * leaves;
  const gstRupees = costRupees * 0.18;
  return Promise.resolve({ freeLeft, costRupees, gstRupees, blockedReason });
};

export const requestChequeBook = (leaves: number): Promise<ChequeBook> => {
  const db = loadDb();
  ensureWelcomeBook(db);
  return chequeBookQuote(leaves).then((q) => {
    if (q.blockedReason) return Promise.reject(new Error(q.blockedReason));
    const total = q.costRupees + q.gstRupees;
    const gate = assertDebitAllowed(db, total);
    if (!gate.ok) return Promise.reject(new Error(gate.message));
    if (total > 0) {
      addStatementRow(db, { description: 'Cheque book charges', amountRupees: -total, category: 'charges' });
    }
    const lastTo = Math.max(...db.phase4.chequeBooks.map((b) => b.to));
    const book: ChequeBook = {
      id: nid('cb'),
      leaves,
      from: lastTo + 1,
      to: lastTo + leaves,
      stage: 'REQUESTED',
      reference: makeReference('CB'),
      requestedAt: mockNow(db),
      delivered: false,
      leafList: [],
    };
    db.phase4.chequeBooks.push(book);
    addNotification(db, 'Cheque book requested', `Reference ${book.reference}. The book will be posted to your communication address.`);
    saveDb(db);
    return book;
  });
};

const findLeaf = (db: MockDb, number: number): { book: ChequeBook; leaf: ChequeLeaf } | undefined => {
  for (const book of db.phase4.chequeBooks) {
    const leaf = book.leafList.find((l) => l.number === number);
    if (leaf) return { book, leaf };
  }
  return undefined;
};

export const stopCheques = (input: {
  from: number;
  to: number;
  reason: string;
  date?: string;
  amountRupees?: number;
  payee?: string;
}): Promise<{ reference: string; costRupees: number }> => {
  const db = loadDb();
  ensureWelcomeBook(db);
  const numbers: number[] = [];
  for (let n = input.from; n <= input.to; n++) numbers.push(n);

  for (const n of numbers) {
    const found = findLeaf(db, n);
    if (!found) return Promise.reject(new Error("This cheque number isn't in a book issued to your account."));
    if (found.leaf.status === 'STOPPED') return Promise.reject(new Error('This cheque is already stopped.'));
    if (found.leaf.status === 'PAID' || found.leaf.status === 'IN_CLEARING' || found.leaf.status === 'RETURNED') {
      return Promise.reject(new Error("This cheque has already been processed and can't be stopped."));
    }
  }

  const isRange = numbers.length > 1;
  const costRupees = (isRange ? 500 : 100 * numbers.length) * 1.18;
  const gate = assertDebitAllowed(db, costRupees);
  if (!gate.ok) return Promise.reject(new Error(gate.message));

  const reference = makeReference('SP');
  const now = mockNow(db);
  for (const n of numbers) {
    const found = findLeaf(db, n);
    if (found) {
      found.leaf.status = 'STOPPED';
      found.leaf.stoppedAt = now;
      found.leaf.stopReason = input.reason;
      found.leaf.stopReference = reference;
      if (input.payee) found.leaf.payee = input.payee;
      if (input.amountRupees) found.leaf.amountRupees = input.amountRupees;
    }
  }
  addStatementRow(db, { description: `Stop payment (${numbers.length} cheque${isRange ? 's' : ''})`, amountRupees: -costRupees, category: 'charges', reference });
  addNotification(db, 'Cheque stopped', `Reference ${reference}.`);
  saveDb(db);
  return Promise.resolve({ reference, costRupees });
};

export const revokeStop = (chequeNumber: number): Promise<void> => {
  const db = loadDb();
  const found = findLeaf(db, chequeNumber);
  if (!found || found.leaf.status !== 'STOPPED') return Promise.reject(new Error('This cheque is not stopped.'));
  found.leaf.status = 'UNUSED';
  found.leaf.stoppedAt = undefined;
  found.leaf.stopReason = undefined;
  saveDb(db);
  return Promise.resolve();
};

// ---------- Positive Pay ----------

export const positivePay = (): Promise<PositivePayEntry[]> => Promise.resolve(loadDb().phase4.positivePay);

export const addPositivePay = (input: {
  chequeNumber: number;
  chequeDate: string;
  payee: string;
  amountRupees: number;
}): Promise<PositivePayEntry> => {
  const db = loadDb();
  ensureWelcomeBook(db);
  if (input.amountRupees < 50_000) return Promise.reject(new Error('Positive Pay is for cheques of ₹50,000 or more.'));
  if (db.phase4.positivePay.some((p) => p.chequeNumber === input.chequeNumber && p.status === 'REGISTERED')) {
    return Promise.reject(new Error('Positive Pay is already registered for this cheque. Cancel it to re-enter.'));
  }
  const found = findLeaf(db, input.chequeNumber);
  if (!found) return Promise.reject(new Error("This cheque number isn't in a book issued to your account."));
  if (found.leaf.status !== 'UNUSED') return Promise.reject(new Error("This cheque has been presented. Positive Pay can't change now."));

  const entry: PositivePayEntry = {
    id: nid('pp'),
    ...input,
    status: 'REGISTERED',
    createdAt: mockNow(db),
  };
  db.phase4.positivePay.push(entry);
  addNotification(db, 'Positive Pay registered', `Cheque ${input.chequeNumber} for ₹${input.amountRupees}. Cheques clear the same working day.`);
  saveDb(db);
  return Promise.resolve(entry);
};

export const cancelPositivePay = (id: string): Promise<void> => {
  const db = loadDb();
  const entry = db.phase4.positivePay.find((p) => p.id === id);
  if (!entry) return Promise.resolve();
  const found = findLeaf(db, entry.chequeNumber);
  if (found && found.leaf.status !== 'UNUSED') {
    return Promise.reject(new Error("This cheque has been presented. Positive Pay can't change now."));
  }
  entry.status = 'CANCELLED';
  saveDb(db);
  return Promise.resolve();
};

// ============================================================ RE-KYC (§5.2)

const RISK_PERIOD_YEARS: Record<RekycState['risk'], number> = { LOW: 10, MEDIUM: 8, HIGH: 2 };

export const rekyc = (): Promise<RekycState & { restrictsDebits: boolean; usableUntil?: number; status: 'UP_TO_DATE' | 'DUE' | 'OVERDUE' }> => {
  const db = loadDb();
  const r = db.phase4.rekyc;
  const now = mockNow(db);
  const daysToDue = Math.ceil((r.dueAt - now) / DAY);
  const status = daysToDue > 90 ? 'UP_TO_DATE' : daysToDue >= 0 ? 'DUE' : 'OVERDUE';
  const restrictsDebits = rekycRestrictsDebits(db);
  const usableUntil = r.risk === 'LOW' ? Math.max(r.dueAt + 365 * DAY, Date.UTC(2026, 5, 30)) : r.dueAt + 90 * DAY;
  return Promise.resolve({ ...r, restrictsDebits, usableUntil, status });
};

const completeRekyc = (db: MockDb): void => {
  const now = mockNow(db);
  db.phase4.rekyc.lastUpdatedAt = now;
  db.phase4.rekyc.dueAt = now + RISK_PERIOD_YEARS[db.phase4.rekyc.risk] * 365 * DAY;
  db.phase4.rekyc.remindersSent = [];
  addNotification(db, 'Re-KYC completed', `Next due ${new Date(db.phase4.rekyc.dueAt).toLocaleDateString('en-IN')}. Debits are unblocked.`);
};

export const rekycNoChange = (): Promise<void> => {
  const db = loadDb();
  completeRekyc(db);
  saveDb(db);
  return Promise.resolve();
};

const PIN_TABLE: Record<string, { city: string; state: string }> = {
  '56': { city: 'Bengaluru', state: 'Karnataka' },
  '40': { city: 'Mumbai', state: 'Maharashtra' },
  '11': { city: 'New Delhi', state: 'Delhi' },
};

export const lookupPin = (pin: string): { city: string; state: string } | null =>
  PIN_TABLE[pin.slice(0, 2)] ?? null;

export const rekycAddressChange = (input: {
  house: string; street: string; area: string; pin: string; city: string; state: string;
}): Promise<void> => {
  const db = loadDb();
  db.phase4.rekyc.addressChange = { status: 'UNDER_VERIFICATION', ...input, submittedAt: mockNow(db) };
  addNotification(db, 'Address change under verification', 'We will confirm your new address shortly.');
  saveDb(db);
  return Promise.resolve();
};

// ============================================================ GST (§5.3)

const GST_STATE_NAMES: Record<string, string> = {
  '27': 'Maharashtra', '29': 'Karnataka', '07': 'Delhi', '33': 'Tamil Nadu', '36': 'Telangana', '24': 'Gujarat', '19': 'West Bengal', '09': 'Uttar Pradesh', '06': 'Haryana', '32': 'Kerala',
};

const MOD36 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** GSTIN check character (spec §5.3). */
export const gstinCheckChar = (first14: string): string => {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const val = MOD36.indexOf(first14[i]);
    const product = val * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(product / 36) + (product % 36);
  }
  return MOD36[(36 - (sum % 36)) % 36];
};

export const validGstinFormat = (gstin: string): boolean => {
  if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin)) return false;
  if (!GST_STATE_NAMES[gstin.slice(0, 2)]) return false;
  return gstin[14] === gstinCheckChar(gstin.slice(0, 14));
};

export const gstRegistrations = (): Promise<GstRegistration[]> => Promise.resolve(loadDb().phase4.gstRegistrations);

export const lookupGstin = (gstin: string): Promise<{ legalName: string; tradeName: string; stateCode: string; stateName: string; address: string; active: boolean; panMatch: boolean }> => {
  const db = loadDb();
  if (!validGstinFormat(gstin)) return Promise.reject(new Error('Enter a valid 15-character GSTIN, like 27ABCDE1234F1Z5.'));
  const panMatch = gstin.slice(2, 12) === db.loans.pan;
  const stateCode = gstin.slice(0, 2);
  return Promise.resolve({
    legalName: 'The Customer',
    tradeName: 'Customer Enterprises',
    stateCode,
    stateName: GST_STATE_NAMES[stateCode] ?? 'Unknown',
    address: `${GST_STATE_NAMES[stateCode]}, India`,
    active: true,
    panMatch,
  });
};

export const addGstin = (gstin: string): Promise<GstRegistration> => {
  const db = loadDb();
  return lookupGstin(gstin).then((look) => {
    if (!look.panMatch) return Promise.reject(new Error("GSTIN doesn't belong to this account's PAN."));
    if (!look.active) return Promise.reject(new Error('This GSTIN is not active.'));
    if (db.phase4.gstRegistrations.some((g) => g.gstin === gstin)) return Promise.reject(new Error('This GSTIN is already on the account.'));
    if (db.phase4.gstRegistrations.some((g) => g.stateCode === look.stateCode)) return Promise.reject(new Error('A GSTIN for this state is already on the account.'));
    const reg: GstRegistration = {
      gstin,
      legalName: look.legalName,
      tradeName: look.tradeName,
      stateCode: look.stateCode,
      stateName: look.stateName,
      address: look.address,
      primary: db.phase4.gstRegistrations.length === 0,
      status: 'ACTIVE',
      addedAt: mockNow(db),
    };
    db.phase4.gstRegistrations.push(reg);
    addNotification(db, 'GSTIN added', `${gstin} (${reg.stateName}). Applies to charges from today onwards.`);
    saveDb(db);
    return reg;
  });
};

export const removeGstin = (gstin: string): Promise<void> => {
  const db = loadDb();
  const wasPrimary = db.phase4.gstRegistrations.find((g) => g.gstin === gstin)?.primary;
  db.phase4.gstRegistrations = db.phase4.gstRegistrations.filter((g) => g.gstin !== gstin);
  if (wasPrimary && db.phase4.gstRegistrations.length > 0) db.phase4.gstRegistrations[0].primary = true;
  saveDb(db);
  return Promise.resolve();
};

export const setPrimaryGstin = (gstin: string): Promise<void> => {
  const db = loadDb();
  db.phase4.gstRegistrations.forEach((g) => { g.primary = g.gstin === gstin; });
  saveDb(db);
  return Promise.resolve();
};

// GST invoices built from the charges in the statement (§5.3).
export const gstInvoices = (): Promise<Array<{ month: string; chargesRupees: number; cgst: number; sgst: number; igst: number; gstin: string }>> => {
  const db = loadDb();
  const primary = db.phase4.gstRegistrations.find((g) => g.primary);
  const byMonth = new Map<string, number>();
  for (const row of db.ledger) {
    if (row.category !== 'charges') continue;
    const d = new Date(row.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    byMonth.set(key, (byMonth.get(key) ?? 0) + Math.abs(row.amountRupees));
  }
  const intra = !primary || primary.stateCode === '27';
  return Promise.resolve(
    [...byMonth.entries()].slice(0, 12).map(([month, chargesRupees]) => ({
      month,
      chargesRupees,
      cgst: intra ? chargesRupees * 0.09 : 0,
      sgst: intra ? chargesRupees * 0.09 : 0,
      igst: intra ? 0 : chargesRupees * 0.18,
      gstin: primary?.gstin ?? 'B2C — no GSTIN',
    })),
  );
};

// ============================================================ FATCA (§5.4)

export const fatca = (): Promise<FatcaRecord> => Promise.resolve(loadDb().phase4.fatca);

export const updateFatca = (input: Omit<FatcaRecord, 'lastUpdatedAt' | 'pendingReview'>): Promise<{ review?: string }> => {
  const db = loadDb();
  const current = db.phase4.fatca;
  const becomingForeign = !current.foreignTaxResident && input.foreignTaxResident;
  const usChanged = current.usPerson !== input.usPerson;
  const leavingForeign = current.foreignTaxResident && !input.foreignTaxResident;

  if (becomingForeign || usChanged || leavingForeign) {
    const reference = makeReference('SR');
    db.phase4.fatca = { ...input, lastUpdatedAt: current.lastUpdatedAt, pendingReview: { reference, submittedAt: mockNow(db) } };
    addNotification(db, 'FATCA under review', `Reference ${reference}. We may contact you for documents.`);
    saveDb(db);
    return Promise.resolve({ review: reference });
  }
  db.phase4.fatca = { ...input, lastUpdatedAt: mockNow(db) };
  saveDb(db);
  return Promise.resolve({});
};

export const fatcaReview = (): { pending?: string } => {
  const p = loadDb().phase4.fatca.pendingReview;
  return { pending: p?.reference };
};

// ============================================================ FAVOURITES (§5.5)

export const favourites = (): Promise<Favourite[]> =>
  Promise.resolve([...loadDb().phase4.favourites].sort((a, b) => a.order - b.order));

export const addFavourite = (input: Omit<Favourite, 'id' | 'order'>): Promise<Favourite> => {
  const db = loadDb();
  const order = Math.max(0, ...db.phase4.favourites.map((f) => f.order)) + 1;
  const fav: Favourite = { ...input, id: nid('fv'), order };
  db.phase4.favourites.push(fav);
  saveDb(db);
  return Promise.resolve(fav);
};

export const renameFavourite = (id: string, label: string): Promise<void> => {
  const db = loadDb();
  const f = db.phase4.favourites.find((x) => x.id === id);
  if (f) f.label = label.slice(0, 24);
  saveDb(db);
  return Promise.resolve();
};

export const removeFavourite = (id: string): Promise<void> => {
  const db = loadDb();
  db.phase4.favourites = db.phase4.favourites.filter((x) => x.id !== id);
  saveDb(db);
  return Promise.resolve();
};

export const reorderFavourites = (orderedIds: string[]): Promise<void> => {
  const db = loadDb();
  orderedIds.forEach((id, i) => {
    const f = db.phase4.favourites.find((x) => x.id === id);
    if (f) f.order = i;
  });
  saveDb(db);
  return Promise.resolve();
};

// ============================================================ DISPUTES (§5.6/§5.7)

const DISPUTE_TIMELINE_DAYS: Record<Dispute['kind'], number | null> = {
  IMPS: 1, UPI_TRANSFER: 1, UPI_MERCHANT: 5, ATM: 5, CARD: 5, OTHER: null,
};

export const disputableDebits = (impsOnly: boolean): Promise<Array<{ id: string; description: string; amountRupees: number; date: number; openComplaint: boolean }>> => {
  const db = loadDb();
  const since = mockNow(db) - 90 * DAY;
  return Promise.resolve(
    db.ledger
      .filter((r) => r.amountRupees < 0 && r.date >= since && r.category !== 'charges')
      .filter((r) => (impsOnly ? r.description.startsWith('IMPS') : true))
      .map((r) => ({
        id: r.id,
        description: r.description,
        amountRupees: Math.abs(r.amountRupees),
        date: r.date,
        openComplaint: db.phase4.disputes.some((d) => d.txnId === r.id && d.status !== 'RESOLVED'),
      })),
  );
};

export const disputes = (): Promise<Dispute[]> => Promise.resolve(loadDb().phase4.disputes);

export const raiseDispute = (input: {
  txnId: string; txnDescription: string; amountRupees: number; txnDate: number;
  kind: Dispute['kind']; reason: string; description: string;
}): Promise<Dispute> => {
  const db = loadDb();
  if (db.phase4.disputes.some((d) => d.txnId === input.txnId && d.status !== 'RESOLVED')) {
    return Promise.reject(new Error('A complaint is already open on this transaction.'));
  }
  const days = DISPUTE_TIMELINE_DAYS[input.kind];
  const dispute: Dispute = {
    id: nid('cmp'),
    reference: makeReference('CMP'),
    ...input,
    status: 'REGISTERED',
    dueAt: days != null ? input.txnDate + days * DAY : undefined,
    compensationRupees: 0,
    createdAt: mockNow(db),
    heldByBank: false,
  };
  db.phase4.disputes.unshift(dispute);
  addNotification(db, 'Complaint registered', `Reference ${dispute.reference}.`);
  saveDb(db);
  return Promise.resolve(dispute);
};

export const reportFraud = (reason: string): Promise<{ reference: string }> => {
  const db = loadDb();
  const reference = makeReference('FRD');
  db.phase4.fraudReports.unshift({ reference, reason, reportedAt: mockNow(db), lifted: false });
  db.limits.debitsFrozen = true; // every debit frozen at once (§5.7)
  addNotification(db, 'Fraud reported', 'Debits on your account are frozen. Our fraud team will call you within 24 hours. Credits still come in.');
  saveDb(db);
  return Promise.resolve({ reference });
};

// ============================================================ LOCKERS (§5.8)

export const LOCKER_SIZES: Record<LockerSize, { label: string; dimensions: string; rentRupees: number }> = {
  SMALL: { label: 'Small', dimensions: '125 × 175 × 492 mm', rentRupees: 2000 },
  MEDIUM: { label: 'Medium', dimensions: '159 × 210 × 492 mm', rentRupees: 4000 },
  LARGE: { label: 'Large', dimensions: '325 × 210 × 492 mm', rentRupees: 8000 },
  XL: { label: 'Extra Large', dimensions: '325 × 420 × 492 mm', rentRupees: 12000 },
};

const BRANCHES: LockerBranch[] = [
  { id: 'fort', name: 'Fort', city: 'Mumbai', availability: { SMALL: 2, MEDIUM: 1, LARGE: 0, XL: 0 } },
  { id: 'andheri', name: 'Andheri', city: 'Mumbai', availability: { SMALL: 2, MEDIUM: 1, LARGE: 0, XL: 0 } },
  { id: 'indiranagar', name: 'Indiranagar', city: 'Bengaluru', availability: { SMALL: 2, MEDIUM: 1, LARGE: 0, XL: 0 } },
];

export const lockerAvailability = (): Promise<LockerBranch[]> => Promise.resolve(BRANCHES);

export const lockers = (): Promise<{ lockers: Locker[]; waitlist: LockerWaitlist[] }> => {
  const db = loadDb();
  return Promise.resolve({ lockers: db.phase4.lockers, waitlist: db.phase4.lockerWaitlist });
};

export const joinLockerWaitlist = (branchId: string, size: LockerSize): Promise<LockerWaitlist> => {
  const db = loadDb();
  const waitNumber = db.phase4.lockerWaitCounter++;
  const place: LockerWaitlist = { id: nid('lk'), branchId, size, waitNumber, joinedAt: mockNow(db) };
  db.phase4.lockerWaitlist.push(place);
  addNotification(db, 'Locker waitlist', `Application received. Your wait list number is ${waitNumber}.`);
  saveDb(db);
  return Promise.resolve(place);
};

export const applyForLocker = (input: {
  branchId: string; size: LockerSize; joint: boolean; secondHirer?: string;
  mode?: 'EITHER_OR_SURVIVOR' | 'JOINTLY'; nominees: Array<{ name: string; relationship: string }>;
}): Promise<Locker> => {
  const db = loadDb();
  const size = LOCKER_SIZES[input.size];
  const total = size.rentRupees * 1.18;
  const gate = assertDebitAllowed(db, total);
  if (!gate.ok) return Promise.reject(new Error(gate.message));
  addStatementRow(db, { description: `Locker rent (year 1) — ${size.label}`, amountRupees: -total, category: 'charges' });
  const locker: Locker = {
    id: nid('lk'),
    number: `S-${100 + db.phase4.lockers.length + 1}`,
    branchId: input.branchId,
    size: input.size,
    joint: input.joint,
    secondHirer: input.secondHirer,
    mode: input.mode,
    nominees: input.nominees,
    rentRupees: size.rentRupees,
    dueAt: mockNow(db) + 365 * DAY,
    autoDebit: true,
    visits: [],
    reference: makeReference('LK'),
  };
  db.phase4.lockers.push(locker);
  // consume any offered waitlist place
  db.phase4.lockerWaitlist = db.phase4.lockerWaitlist.filter((w) => !(w.branchId === input.branchId && w.size === input.size));
  addNotification(db, 'Locker allotted', `Locker allotted: ${locker.number}, ${input.branchId}. Visit the branch with ID to collect the key and give a specimen signature.`);
  saveDb(db);
  return Promise.resolve(locker);
};

export const setLockerAutoDebit = (id: string, on: boolean): Promise<void> => {
  const db = loadDb();
  const l = db.phase4.lockers.find((x) => x.id === id);
  if (l) l.autoDebit = on;
  saveDb(db);
  return Promise.resolve();
};

export const recordLockerVisit = (id: string): Promise<void> => {
  const db = loadDb();
  const l = db.phase4.lockers.find((x) => x.id === id);
  if (l) {
    l.visits.unshift(mockNow(db));
    addNotification(db, 'Locker visit', `Visit logged to locker ${l.number}. An SMS has been sent.`);
  }
  saveDb(db);
  return Promise.resolve();
};

export const surrenderLocker = (id: string): Promise<{ refundRupees: number }> => {
  const db = loadDb();
  const idx = db.phase4.lockers.findIndex((x) => x.id === id);
  if (idx < 0) return Promise.reject(new Error('Locker not found.'));
  const l = db.phase4.lockers[idx];
  const daysLeft = Math.max(0, Math.ceil((l.dueAt - mockNow(db)) / DAY));
  const refundRupees = Math.round((l.rentRupees * daysLeft) / 365);
  if (refundRupees > 0) addStatementRow(db, { description: `Locker ${l.number} surrender refund`, amountRupees: refundRupees, category: 'charges' });
  db.phase4.lockers.splice(idx, 1);
  addNotification(db, 'Locker surrendered', `${l.number} surrendered. Return the key at the branch.`);
  saveDb(db);
  return Promise.resolve({ refundRupees });
};

// ============================================================ NOMINEE (§5.9, read-only)

export const accountNominee = (): Promise<{ recorded: boolean; name?: string; relationship?: string; dob?: string; note: string }> => {
  const db = loadDb();
  const n = db.nominees[0];
  return Promise.resolve({
    recorded: !!n,
    name: n?.name,
    relationship: n?.relationship,
    note: 'Adding or changing nominees here is coming soon. Until then, visit your branch to update your nominee.',
  });
};

// ============================================================ clock step

// re-KYC reminders (3 before, 3 after; only one per clock move, the latest due).
registerClockStep((db, now) => {
  const r = db.phase4.rekyc;
  const daysToDue = Math.ceil((r.dueAt - now) / DAY);
  const marks: Array<{ key: string; when: number }> = [
    { key: 'pre90', when: 90 }, { key: 'pre60', when: 60 }, { key: 'pre30', when: 30 },
    { key: 'post1', when: -1 }, { key: 'post30', when: -30 }, { key: 'post60', when: -60 },
  ];
  const due = [...marks].reverse().find((m) => daysToDue <= m.when && !r.remindersSent.includes(m.key));
  if (due) {
    r.remindersSent.push(due.key);
    const text = daysToDue >= 0 ? `Your Re-KYC is due in ${daysToDue} day(s).` : `Your Re-KYC is overdue by ${-daysToDue} day(s).`;
    addNotification(db, 'Re-KYC reminder', `${text} (sent by SMS and email too).`, now);
  }
  // cheque book tracker auto-advance one stage per clock move
  const ip = db.phase4.chequeBooks.find((b) => !b.delivered);
  if (ip) {
    if (ip.stage === 'REQUESTED') ip.stage = 'PRINTED';
    else if (ip.stage === 'PRINTED') { ip.stage = 'DISPATCHED'; ip.awb = `AWB${Math.floor(1e9 + Math.random() * 9e9)}`; }
    else if (ip.stage === 'DISPATCHED') {
      ip.stage = 'DELIVERED';
      ip.delivered = true;
      ip.leafList = Array.from({ length: ip.leaves }, (_, i) => ({ number: ip.from + i, status: 'UNUSED' as const }));
      addNotification(db, 'Cheque book delivered', `Book ${ip.from}–${ip.to} is delivered and ready to use.`, now);
    }
  }
  // disputes: move to other bank, auto-reverse T+1/T+5 unless held
  for (const d of db.phase4.disputes) {
    if (d.status === 'REGISTERED') d.status = 'WITH_OTHER_BANK';
    if (d.status === 'WITH_OTHER_BANK' && !d.heldByBank && d.dueAt && now >= d.dueAt && !d.resolution) {
      d.status = 'RESOLVED';
      d.resolution = 'REVERSED';
      const lateDays = Math.max(0, Math.floor((now - d.dueAt) / DAY));
      d.compensationRupees = lateDays * 100;
      addStatementRow(db, { description: `Complaint ${d.reference} reversal`, amountRupees: d.amountRupees, category: 'bill', reference: d.reference }, now);
      if (d.compensationRupees > 0) {
        addStatementRow(db, { description: `Complaint ${d.reference} compensation`, amountRupees: d.compensationRupees, category: 'interest', reference: d.reference }, now);
      }
      addNotification(db, 'Complaint resolved', `${d.reference}: reversed${d.compensationRupees ? ` with ₹${d.compensationRupees} compensation` : ''}.`, now);
    } else if (d.status === 'WITH_OTHER_BANK' && d.dueAt && now > d.dueAt) {
      d.compensationRupees = Math.floor((now - d.dueAt) / DAY) * 100;
    }
  }
});
